import React, { ReactNode, useState } from 'react'
import { Pressable, Text, View } from 'react-native'
import styled from 'styled-components/native'
import { Feather } from '../../../components/icon/vectorIcons'
import useTranslation from '../../../hooks/useTranslation'
import { builderColors, builderRadii } from './builderColors'

interface BlockFrameProps {
  label: string
  selected: boolean
  isFirst: boolean
  isLast: boolean
  onSelect: () => void
  onMoveUp: () => void
  onMoveDown: () => void
  onDuplicate: () => void
  onRemove: () => void
  children: ReactNode
}

// Pulls the frame outside the block's own layout so selecting a block doesn't
// shift the page content.
const FRAME_INSET = 8

/**
 * Builder chrome around a block: hover/selected outline, the block-type tag and
 * the move / duplicate / delete toolbar.
 */
const BlockFrame = ({
  label,
  selected,
  isFirst,
  isLast,
  onSelect,
  onMoveUp,
  onMoveDown,
  onDuplicate,
  onRemove,
  children
}: BlockFrameProps) => {
  const { t } = useTranslation()
  const [hovered, setHovered] = useState(false)

  return (
    <Pressable
      onPress={onSelect}
      // Marks the block, so the canvas can tell a press that started inside
      // one (react-native-web renders data-doclet-block)
      {...({ dataSet: { docletBlock: 'true' } } as any)}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      accessibilityLabel={label}
      style={{
        marginHorizontal: -FRAME_INSET,
        paddingHorizontal: FRAME_INSET - 1,
        paddingTop: FRAME_INSET - 1,
        paddingBottom: 1,
        borderWidth: 1,
        borderRadius: builderRadii.xs,
        borderColor: selected
          ? builderColors.selectedBorder
          : hovered
          ? builderColors.hoverBorder
          : 'transparent',
        backgroundColor: selected ? builderColors.selectedFill : 'transparent',
        // Pressable's pointer would carry into the block's text: let the
        // browser pick (a text cursor over text, the arrow elsewhere)
        cursor: 'auto'
      }}
    >
      {selected && (
        <>
          <Tag>
            <TagText>{label.toUpperCase()}</TagText>
          </Tag>
          <Toolbar>
            <ToolbarButton
              icon="arrow-up"
              label={t('doclets:builder:moveUp')}
              disabled={isFirst}
              onPress={onMoveUp}
            />
            <ToolbarButton
              icon="arrow-down"
              label={t('doclets:builder:moveDown')}
              disabled={isLast}
              onPress={onMoveDown}
            />
            <ToolbarButton
              icon="copy"
              label={t('doclets:builder:duplicate')}
              onPress={onDuplicate}
            />
            <ToolbarButton
              icon="x"
              label={t('doclets:builder:remove')}
              onPress={onRemove}
            />
          </Toolbar>
        </>
      )}
      {children}
    </Pressable>
  )
}

interface ToolbarButtonProps {
  icon: React.ComponentProps<typeof Feather>['name']
  label: string
  disabled?: boolean
  onPress: () => void
}

const ToolbarButton = ({
  icon,
  label,
  disabled = false,
  onPress
}: ToolbarButtonProps) => (
  <Pressable
    accessibilityRole="button"
    accessibilityLabel={label}
    accessibilityState={{ disabled }}
    disabled={disabled}
    onPress={onPress}
    style={{
      width: 28,
      height: 28,
      marginLeft: 4,
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: builderRadii.md,
      borderWidth: 1,
      borderColor: builderColors.border,
      backgroundColor: '#FFFFFF',
      opacity: disabled ? 0.35 : 1
    }}
  >
    <Feather name={icon} size={14} color={builderColors.text} />
  </Pressable>
)

const Tag = styled(View)`
  position: absolute;
  top: -10px;
  left: ${FRAME_INSET - 1}px;
  z-index: 1;
  padding: 2px 6px;
  border-radius: ${builderRadii.xs}px;
  background-color: ${builderColors.tag};
`

const TagText = styled(Text)`
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
`

const Toolbar = styled(View)`
  position: absolute;
  top: -14px;
  right: ${FRAME_INSET}px;
  z-index: 1;
  flex-direction: row;
`

export default BlockFrame
